import { useMemo, useState } from 'react';
import { useList } from '@hana/data';
import { Button, Segmented } from '@hana/ui';
import { findPort } from '@hana/domain';
import { useT } from '@hana/i18n';
import { IconCalendarCheck, IconCalendarTime, IconChevronDown, IconFilter, IconShip } from '@tabler/icons-react';
import { dmHm } from './format';
import type { Sailing, Vessel } from './types';
import { VesselDrawer } from './VesselDrawer';
import './SchedList.i18n';
import s from './sched.module.css';

const cityOf = (code: string) => findPort(code)?.name ?? code;
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WD = ['sl.wd0', 'sl.wd1', 'sl.wd2', 'sl.wd3', 'sl.wd4', 'sl.wd5', 'sl.wd6'];
/** Chỗ còn theo chuyến (board Mint-P2P: SPACE — chờ nguồn hãng) và lệch ETD dự báo (giờ, FC) — dữ liệu mẫu theo board. */
const SPACE: Record<string, 'soon' | 'full'> = { 'SINAR SANUR|152S': 'soon', 'KMTC PUSAN|2612S': 'soon', 'MAERSK PORT KLANG|643N': 'full' };
const FC_H: Record<string, number> = { 'KMTC PUSAN|2612S': 5, 'YM CERTAINTY|089N': 18, 'SINAR SANUR|152S': 26, 'SAWASDEE VEGA|2613S': 0, 'ONE ALTAIR|076E': -6, 'MAERSK PORT KLANG|643N': 10 };
const WD_SHORT = ['sl.ws0', 'sl.ws1', 'sl.ws2', 'sl.ws3', 'sl.ws4', 'sl.ws5', 'sl.ws6'];
// Ngày trong tuần của mốc ISO có múi giờ VN: đọc phần ngày, không qua múi giờ máy.
const dow = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00Z`).getUTCDay();
const dayMon = (iso: string) => `${iso.slice(8, 10)}/${MON[Number(iso.slice(5, 7)) - 1]}`;
const ms = (iso: string) => new Date(iso).getTime();

/** "còn 1 ngày 13 giờ" · "còn 20 giờ 00'" · "đã cắt" — tính từ `now` (dữ liệu mẫu neo ngày của board). */
function countdown(t: (key: string, vars?: Record<string, string | number>) => string, cut: string, now: number): { k: string; warn: boolean } {
  const m = Math.floor((ms(cut) - now) / 60000);
  if (m <= 0) return { k: t('sl.cutDone'), warn: true };
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  if (d >= 1) return { k: t('sl.leftDH', { d, h }), warn: false };
  return { k: t('sl.leftHM', { h, m: String(m % 60).padStart(2, '0') }), warn: true };
}

type Tt = 'all' | '7' | '14';
const TT_OPTS: { key: Tt; label: string }[] = [
  { key: 'all', label: 'sl.ttAll' },
  { key: '7', label: 'sl.tt7' },
  { key: '14', label: 'sl.tt14' },
];

export function SchedList({
  sails,
  now,
  picked,
  onPick,
}: {
  sails: readonly Sailing[];
  now: number;
  picked: ReadonlySet<string>;
  onPick: (ids: ReadonlySet<string>) => void;
}) {
  const t = useT();
  const [car, setCar] = useState<string | null>(null);
  const [tt, setTt] = useState<Tt>('all');
  const [direct, setDirect] = useState(false);
  const [svc, setSvc] = useState<string>('all');
  const [fOpen, setFOpen] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [vesselKey, setVesselKey] = useState<string | null>(null);
  const vessels = useList('vessel');
  const profiles = useMemo(() => new Map(((vessels.data?.items ?? []) as Vessel[]).map((v) => [v.id, v])), [vessels.data]);

  const carsOf = (v: Sailing) => [v.carrier, ...(v.coCarriers ?? [])];
  const carriers = [...new Set(sails.flatMap(carsOf))];
  const services = [...new Set(sails.map((v) => v.service).filter((x): x is string => !!x))];
  const rows = sails.filter(
    (v) => (!car || carsOf(v).includes(car)) && (tt === 'all' || v.transitDays <= Number(tt)) && (!direct || !v.via) && (svc === 'all' || v.service === svc),
  );
  const nFilter = (tt !== 'all' ? 1 : 0) + (direct ? 1 : 0) + (svc !== 'all' ? 1 : 0);
  const allOn = rows.length > 0 && rows.every((v) => picked.has(v.id));
  const toggle = (id: string) => {
    const n = new Set(picked);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    onPick(n);
  };
  const toggleAll = () => {
    const n = new Set(picked);
    for (const v of rows) {
      if (allOn) n.delete(v.id);
      else n.add(v.id);
    }
    onPick(n);
  };

  return (
    <section className={s.card} aria-label={t('sl.title')}>
      <div className={s.bar}>
        <label className={s.all}>
          <input type="checkbox" checked={allOn} onChange={toggleAll} aria-label={t('sl.selAll')} />
          {t('sl.all')}
        </label>
        <div className={s.chips} role="group" aria-label={t('sl.byCarrier')}>
          <button type="button" aria-pressed={!car} onClick={() => setCar(null)}>
            {t('sl.allCarriers')} <span>{sails.length}</span>
          </button>
          {carriers.map((c) => (
            <button key={c} type="button" aria-pressed={car === c} onClick={() => setCar(car === c ? null : c)}>
              {c} <span>{sails.filter((v) => carsOf(v).includes(c)).length}</span>
            </button>
          ))}
        </div>
        <span className={s.grow} />
        <div className={s.flt}>
          <Button variant="soft" size="sm" aria-haspopup="dialog" aria-expanded={fOpen} title={t('sl.filter')} icon={<IconFilter size={16} aria-hidden="true" />} onClick={() => setFOpen(!fOpen)}>
            {t('sl.filterBtn')}{nFilter ? ` · ${nFilter}` : ''} ▾
          </Button>
          {fOpen && (
            <div className={s.pop} role="dialog" aria-label={t('sl.filter')}>
              <div className={s.grp}>
                <span>Transit</span>
                <Segmented kind="toggle" label="Transit" value={tt} onChange={setTt} items={TT_OPTS.map((o) => ({ ...o, label: t(o.label) }))} />
              </div>
              <div className={s.grp}>
                <span>{t('sl.route')}</span>
                <Segmented
                  kind="toggle"
                  label={t('sl.route')}
                  value={direct ? 'D' : 'A'}
                  onChange={(k) => setDirect(k === 'D')}
                  items={[{ key: 'A', label: t('sl.direct+ts') }, { key: 'D', label: t('sl.directOnly') }]}
                />
              </div>
              <div className={s.grp}>
                <span>Service</span>
                <Segmented
                  kind="toggle"
                  label="Service"
                  value={svc}
                  onChange={setSvc}
                  items={[{ key: 'all', label: t('sl.allSvc') }, ...services.map((x) => ({ key: x, label: x }))]}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {rows.length === 0 && (
        <div className={s.empty}>
          <b>{t('sl.noMatch')}</b>
          <div>{t('sl.noMatchD')}</div>
        </div>
      )}
      <div className={s.list} role="list" aria-label={t('sl.sailings')}>
        {rows.map((v) => {
          const cd = countdown(t, v.cyCutoff, now);
          const on = picked.has(v.id);
          const name = `${v.vessel} ${v.voyage}`;
          const key = `${v.vessel}|${v.voyage}`;
          const sp = SPACE[key];
          const fc = FC_H[key] ?? 0;
          const cut = `${t(WD_SHORT[dow(v.cyCutoff)] ?? 'sl.ws0')} ${dmHm(v.cyCutoff)}`;
          return (
            <article key={v.id} role="listitem" className={`${s.vc} ${on ? s.on : ''}`} aria-label={t('sl.sailing', { name })}>
              <input className={s.sel} type="checkbox" checked={on} onChange={() => toggle(v.id)} aria-label={t('sl.pick', { name })} title={t('sl.pickTip')} />
              <div className={s.axisBox}>
                <div>
                  <div className={s.lb}><IconCalendarTime size={14} aria-hidden="true" />ETD</div>
                  <b className={s.big}>{dayMon(v.etd)}</b>
                  <div className={s.sub}>{t(WD[dow(v.etd)] ?? 'sl.wd0')}</div>
                </div>
                <div className={s.axis} role="img" aria-label={`${t('sl.days', { n: v.transitDays })}, ${v.via ? t('sl.via', { v: v.via }) : t('sl.directLow')}`}>
                  <b>{t('sl.days', { n: v.transitDays })}</b>
                  <div className={s.line}><i /><span /><em /></div>
                  <div className={s.ends}>
                    <span title={cityOf(v.pol)}>{cityOf(v.pol).toUpperCase()}</span>
                    <span>{v.via ? t('sl.via', { v: v.via }) : t('sl.directUp')}</span>
                    <span title={cityOf(v.pod)}>{cityOf(v.pod).toUpperCase()}</span>
                  </div>
                </div>
                <div className={s.right}>
                  <div className={s.lb}>
                    {Math.abs(fc) >= 12 && <span className={`${s.fc} ${fc > 0 ? s.fcLate : s.fcEarly}`} title={t('sl.fcTip')}>{fc > 0 ? '+' : '−'}{Math.abs(fc)}h</span>}
                    <IconCalendarTime size={14} aria-hidden="true" />ETA
                  </div>
                  <b className={s.big}>{dayMon(v.eta)}</b>
                  <div className={s.sub}>{t(WD[dow(v.eta)] ?? 'sl.wd0')}</div>
                </div>
              </div>
              <div className={s.info}>
                <span className={s.lb}><IconShip size={14} aria-hidden="true" />{t('sl.vsl')}</span>
                <b className={s.vsl}>
                  <IconShip size={16} aria-hidden="true" />
                  <span
                    tabIndex={0}
                    className={s.vlink}
                    title={t('sl.openVessel')}
                    onClick={() => setVesselKey(key)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setVesselKey(key);
                      }
                    }}
                  >
                    {name}
                  </span>
                </b>
                <div className={s.sub}>
                  {v.service ?? t('sl.unmapped')}
                  {sp && <span className={sp === 'soon' ? s.spWarn : s.spErr}> · {t(sp === 'soon' ? 'sl.spSoon' : 'sl.spFull')}</span>}
                  {v.teuHeld > 0 ? t('sl.held', { n: v.teuHeld }) : ''}
                </div>
                <div className={s.cut}>
                  <span>{t('sl.cutCy')}</span>
                  <b>{cut}</b>
                  <small className={cd.warn ? s.warn : undefined}>{cd.k}</small>
                </div>
                <div className={s.tags} role="list" aria-label={t('sl.operators', { v: carsOf(v).join(', ') })}>
                  {carsOf(v).map((c) => (
                    <span key={c} role="listitem">{c}</span>
                  ))}
                </div>
              </div>
              <div className={s.act}>
                {/* TODO: Book mở Booking khi luồng D có route nhận chuyến. */}
                <Button variant="primary" icon={<IconCalendarCheck size={16} aria-hidden="true" />} title={t('sl.bookTip')}>
                  Book
                </Button>
                <button
                  type="button"
                  className={s.more}
                  aria-expanded={open === v.id}
                  aria-label={t('sl.detailAria', { name })}
                  title={t('sl.detailTip')}
                  onClick={() => setOpen(open === v.id ? null : v.id)}
                >
                  <IconChevronDown size={18} aria-hidden="true" style={open === v.id ? { transform: 'rotate(180deg)' } : undefined} />
                </button>
              </div>
              {open === v.id && (
                <dl className={s.detail} aria-label="Cut-off">
                  <div><dt>SI</dt><dd>{dmHm(v.siCutoff)}</dd></div>
                  <div><dt>CY</dt><dd>{dmHm(v.cyCutoff)}</dd></div>
                  <div><dt>VGM</dt><dd>{dmHm(v.vgmCutoff)}</dd></div>
                  <div><dt>ICD</dt><dd>{dmHm(v.icdCutoff)}</dd></div>
                  <div><dt>Terminal</dt><dd>{v.terminal}</dd></div>
                </dl>
              )}
            </article>
          );
        })}
      </div>
      <VesselDrawer vessel={vesselKey ? (profiles.get(vesselKey) ?? null) : null} onClose={() => setVesselKey(null)} />
    </section>
  );
}
